import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Event } from '../../models/event/event';
import { DateUtil } from '../../utils/date-util';
import { cn } from '../../utils/class-names-util';
import { NavigationUtil } from '../../utils/navigation-util';

interface UpcomingEventsTimelineProps {
  events: Event[];
  className?: string;
}

const UpcomingEventsTimeline: React.FC<UpcomingEventsTimelineProps> = ({
  events,
  className
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const handleEventClick = (event: Event) => {
    NavigationUtil.navigateToEvent(event, navigate);
  };

  // Limit to 3 events
  const displayEvents = events.slice(0, 3);

  if (displayEvents.length === 0) {
    return null;
  }

  return (
    <div className={cn("bg-white", className)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {t('pages.events.upcomingEvents')}
          </h2>
          <p className="text-gray-600">
            {t('pages.events.showingFirst', { count: 3, total: events.length })}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {displayEvents.map((event, index) => {
            const isFeatured = index === 0;

            return (
              <div
                key={event.id}
                className={cn(
                  "relative overflow-hidden rounded-2xl transition-all duration-300 hover:shadow-xl",
                  isFeatured 
                    ? "lg:col-span-2 lg:row-span-2 bg-gradient-to-br from-black via-gray-900 to-black text-white" 
                    : "bg-white border border-gray-200 shadow-lg hover:shadow-xl"
                )}
              >
                {/* Background Image */}
                {(event.media.cover?.url || '') && (
                  <div className="absolute inset-0">
                    <img
                      src={event.media.cover?.url || ''}
                      alt={event.title}
                      className={cn(
                        "w-full h-full object-cover",
                        isFeatured ? "opacity-30" : "opacity-10"
                      )}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {/* Content */}
                <div className={cn(
                  "relative z-10 p-6",
                  isFeatured ? "lg:p-8" : "lg:p-6"
                )}>
                  {/* Date Badge */}
                  <div className="flex items-center justify-between mb-4">
                    <div className={cn(
                      "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium",
                      isFeatured 
                        ? "bg-white bg-opacity-20 backdrop-blur-sm text-white" 
                        : "bg-blue-100 text-blue-800"
                    )}>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" />
                      </svg>
                      {DateUtil.formatDate(event.startDate, i18n.language)}
                    </div>
                    {/* Removed days away badge for cleaner UI */}
                  </div>

                  {/* Title */}
                  <h3 className={cn(
                    "font-bold mb-3 line-clamp-2",
                    isFeatured 
                      ? "text-2xl lg:text-3xl text-white" 
                      : "text-xl text-gray-900"
                  )}>
                    {event.title}
                  </h3>

                  {/* Description */}
                  <p className={cn(
                    "mb-4 line-clamp-3",
                    isFeatured 
                      ? "text-gray-200 text-base" 
                      : "text-gray-600 text-sm"
                  )}>
                    {event.shortDescription}
                  </p>

                  {/* Event Info */}
                  <div className="space-y-2 mb-6">
                    {/* Time */}
                    <div className={cn(
                      "flex items-center text-sm",
                      isFeatured ? "text-gray-200" : "text-gray-600"
                    )}>
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>
                        {DateUtil.formatTime(event.startTime, i18n.language)} - {DateUtil.formatTime(event.endTime, i18n.language)}
                      </span>
                    </div>

                    {/* Location */}
                    <div className={cn(
                      "flex items-center text-sm",
                      isFeatured ? "text-gray-200" : "text-gray-600"
                    )}>
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      <span className="line-clamp-1">
                        {event.address.name}
                      </span>
                    </div>

                    {/* Category */}
                    <div className={cn(
                      "flex items-center text-sm",
                      isFeatured ? "text-gray-200" : "text-gray-600"
                    )}>
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span>{t(`pages.events.categories.${event.category}`)}</span>
                    </div>
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() => handleEventClick(event)}
                    className={cn(
                      "inline-flex items-center justify-center w-full py-3 px-6 rounded-lg font-medium transition-all duration-300",
                      isFeatured 
                        ? "bg-white text-black hover:bg-gray-100" 
                        : "bg-black text-white hover:bg-gray-800"
                    )}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    {t('pages.events.card.viewDetails')}
                  </button>
                </div>

                {/* Featured Badge */}
                {isFeatured && (
                  <div className="absolute top-4 right-4">
                    <div className="bg-white bg-opacity-20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
                      {t('pages.events.card.featured')}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* View All Button */}
        {events.length > 3 && (
          <div className="text-center mt-8">
            <a
              href="#all-events"
              className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 font-medium"
            >
              {t('pages.events.viewAllUpcoming')} ({events.length - 3} {t('pages.events.more')})
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingEventsTimeline;